/**
 * MUTAGIM.SHOP — Netflix Product Card (Phase 6 + Phase 9 + Phase 18)
 * Carte produit style Netflix avec badges dynamiques, bouton Wishlist et Comparer
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Star, ShoppingCart, Eye, Ban, Flame, TrendingUp, Heart, AlertTriangle, Users, GitCompare } from 'lucide-react';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useCart } from '../../contexts/CartContext';
import { useWishlist } from '../../contexts/WishlistContext';
import { useCompare } from '../../contexts/CompareContext';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function ProductCardNetflix({ product, className, isPreviewMode = false }) {
  const { t, i18n } = useTranslation();
  const { formatPrice } = useCurrency();
  const { addToCart } = useCart();
  const { isInWishlist, toggleWishlist } = useWishlist();
  const { addToCompare, isInCompare, canAddMore } = useCompare();
  const { isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const [viewersCount, setViewersCount] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [wishlistLoading, setWishlistLoading] = useState(false);
  
  const previewToken = searchParams.get('token');
  const inPreview = isPreviewMode || !!previewToken;
  const lang = i18n.language;
  const inWishlist = isInWishlist(product.id);
  const inCompare = isInCompare(product.id);

  // Extraire les données
  const name = product.name?.[lang] || product.name?.fr || 'Produit';
  
  // Support both image formats: array or object with main/gallery
  const extractImage = () => {
    const images = product.images;
    if (!images) return '/placeholder-product.jpg';
    
    // Si c'est un tableau
    if (Array.isArray(images)) {
      const first = images[0];
      if (typeof first === 'string') return first;
      if (typeof first === 'object') return first.url || first.src || '/placeholder-product.jpg';
      return '/placeholder-product.jpg';
    }
    
    // Si c'est un objet avec main
    if (images.main) {
      if (Array.isArray(images.main)) {
        const first = images.main[0];
        if (typeof first === 'string') return first;
        if (typeof first === 'object') return first.url || first.src || '/placeholder-product.jpg';
      }
      if (typeof images.main === 'string') return images.main;
    }
    
    return '/placeholder-product.jpg';
  };
  
  const image = extractImage();
  
  // Check if product has QC photos
  const hasQc = product.images?.has_qc || (product.images?.qc && product.images.qc.length > 0);
  
  const rating = product.stats?.average_rating || product.stats?.rating_avg || 0;
  const reviewCount = product.stats?.reviews_count || 0;
  const soldCount = product.badges?.sales_this_month || product.stats?.purchases_count || product.stats?.sales_count || 0;
  const badges = product.badges || {};

  // Récupérer le nombre de viewers (si pas déjà inclus)
  useEffect(() => {
    if (product.viewers?.count !== undefined) {
      setViewersCount(product.viewers.count);
    }
  }, [product.viewers]);

  // Déterminer le badge principal selon la priorité - recalculé quand la langue change
  const badge = useMemo(() => {
    const primaryBadge = badges.primary_badge;
    
    switch (primaryBadge) {
      case 'almost_sold_out':
        return { 
          text: t('badges.almostSoldOut', 'Presque épuisé'), 
          color: 'bg-red-600', 
          icon: AlertTriangle,
          animate: true
        };
      case 'trending':
        return { 
          text: t('badges.trending', 'Tendance'), 
          color: 'bg-orange-500', 
          icon: TrendingUp,
          animate: false
        };
      case 'new':
        return { 
          text: t('badges.new', 'Nouveau'), 
          color: 'bg-green-500', 
          icon: Flame,
          animate: false
        };
      case 'mutagim_pick':
        return { 
          text: t('badges.mutagimPick', 'Sélection Mutagim'), 
          color: 'bg-purple-500', 
          icon: Heart,
          animate: false
        };
      default:
        // Fallback sur les anciens badges
        if (badges.is_almost_sold_out) {
          return { text: t('badges.almostSoldOut'), color: 'bg-red-600', icon: AlertTriangle, animate: true };
        }
        if (badges.is_trending) {
          return { text: t('badges.trending'), color: 'bg-orange-500', icon: TrendingUp };
        }
        if (badges.is_new) {
          return { text: t('badges.new'), color: 'bg-green-500', icon: Flame };
        }
        if (badges.is_mutagim_pick) {
          return { text: t('badges.mutagimPick'), color: 'bg-purple-500', icon: Heart };
        }
        return null;
    }
  }, [badges, t, lang]); // Dépendances: badges, t (traduction), lang (langue)

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (inPreview) {
      toast.info('Mode Preview — Action désactivée');
      return;
    }
    
    await addToCart(product.id);
  };

  const handleToggleWishlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (inPreview) {
      toast.info('Mode Preview — Action désactivée');
      return;
    }
    
    if (!isAuthenticated) {
      toast.error('Connectez-vous pour ajouter aux favoris');
      return;
    }
    
    setWishlistLoading(true);
    await toggleWishlist(product.id, name);
    setWishlistLoading(false);
  };

  // Formater l'image URL
  const getImageUrl = (img) => {
    if (!img) return '/placeholder-product.jpg';
    
    // Si img est un objet, extraire l'URL
    if (typeof img === 'object') {
      img = img.url || img.src || img.main?.[0] || img[0] || '/placeholder-product.jpg';
    }
    
    // Vérifier que img est maintenant une string
    if (typeof img !== 'string') return '/placeholder-product.jpg';
    
    if (img.startsWith('http')) return img;
    // Les images uploadées doivent passer par /api pour le bon content-type
    if (img.startsWith('/uploads/')) return `${API_URL}/api${img}`;
    if (img.startsWith('/api/')) return `${API_URL}${img}`;
    return img;
  };

  return (
    <Link 
      to={`/product/${product.slug}`}
      className={cn(
        "group relative flex flex-col bg-gray-900/80 rounded-lg overflow-hidden",
        "border border-gray-800/50 hover:border-mutagim-red/60 transition-all duration-300",
        "hover:shadow-xl hover:shadow-mutagim-red/20 hover:-translate-y-2 hover:scale-[1.02]",
        "backdrop-blur-sm",
        className
      )}
      data-testid={`product-card-${product.slug}`}
    >
      {/* Image container */}
      <div className="relative aspect-[4/5] overflow-hidden bg-gray-800">
        {/* Placeholder pendant chargement */}
        {!imageLoaded && (
          <div className="absolute inset-0 bg-gray-800 animate-pulse" />
        )}
        
        <img
          src={getImageUrl(image)}
          alt={name}
          className={cn(
            "w-full h-full object-cover transition-all duration-500",
            "group-hover:scale-110",
            imageLoaded ? "opacity-100" : "opacity-0"
          )}
          loading="lazy"
          onLoad={() => setImageLoaded(true)}
        />
        
        {/* Badge dynamique */}
        {badge && (
          <span 
            className={cn(
              "absolute top-2 start-2 px-2 py-1 text-xs font-bold text-white rounded-md",
              "flex items-center gap-1 shadow-lg",
              badge.color,
              badge.animate && "animate-pulse"
            )}
            data-testid="product-badge"
          >
            {badge.icon && <badge.icon className="w-3 h-3" />}
            {badge.text}
          </span>
        )}
        
        {/* Badge QC Vérifié */}
        {hasQc && (
          <span 
            className={cn(
              "absolute px-2 py-1 text-[10px] font-bold text-white rounded-md",
              "flex items-center gap-1 shadow-lg bg-green-600",
              badge ? "top-10 start-2" : "top-2 start-2"  // Position below main badge if exists
            )}
            data-testid="qc-badge"
          >
            ✅ QC
          </span>
        )}
        
        {/* Bouton Wishlist ❤️ */}
        <button
          onClick={handleToggleWishlist}
          disabled={wishlistLoading}
          className={cn(
            "absolute top-2 end-2 w-8 h-8 rounded-full flex items-center justify-center",
            "transition-all duration-200 z-10",
            inWishlist 
              ? "bg-mutagim-red text-white shadow-lg shadow-mutagim-red/40" 
              : "bg-black/50 text-white/80 hover:bg-mutagim-red hover:text-white",
            wishlistLoading && "opacity-50 cursor-not-allowed"
          )}
          data-testid="wishlist-btn"
          aria-label={inWishlist ? "Retirer des favoris" : "Ajouter aux favoris"}
        >
          <Heart 
            className={cn(
              "w-4 h-4 transition-transform",
              inWishlist && "fill-current scale-110"
            )} 
          />
        </button>

        {/* Bouton Comparer */}
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!inCompare) {
              addToCompare({
                id: product.id,
                name: typeof product.name === 'object' 
                  ? (product.name[lang] || product.name.fr || product.name.en || 'Produit')
                  : product.name,
                slug: product.slug,
                price: product.price,
                images: product.images,
                rating: rating,
                reviews_count: reviewCount,
                stock: product.stock,
                sizes: product.variants?.map(v => v.size).filter(Boolean),
                description: typeof product.description === 'object'
                  ? (product.description[lang] || product.description.fr || product.description.en || '')
                  : product.description,
              });
            }
          }}
          disabled={!canAddMore && !inCompare}
          className={cn(
            "absolute top-12 end-2 w-8 h-8 rounded-full flex items-center justify-center",
            "transition-all duration-200 z-10",
            inCompare 
              ? "bg-blue-500 text-white shadow-lg" 
              : "bg-black/50 text-white/80 hover:bg-blue-500 hover:text-white",
            !canAddMore && !inCompare && "opacity-50 cursor-not-allowed"
          )}
          data-testid="compare-btn"
          aria-label={inCompare ? "Dans la comparaison" : "Ajouter à comparer"}
        >
          <GitCompare className="w-4 h-4" />
        </button>
        
        {/* Compteur "X personnes regardent" */}
        {viewersCount >= 3 && (
          <div 
            className="absolute bottom-2 start-2 end-2 px-2 py-1 bg-black/70 backdrop-blur-sm rounded text-xs text-white flex items-center gap-1"
            data-testid="viewers-count"
          >
            <Users className="w-3 h-3 text-green-400" />
            <span>{viewersCount} {t('product.viewersWatching', 'personnes regardent')}</span>
          </div>
        )}
        
        {/* Quick actions overlay */}
        <div className={cn(
          "absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent",
          "opacity-0 group-hover:opacity-100 transition-all duration-300",
          "flex items-end justify-center pb-4 gap-2"
        )}>
          {inPreview ? (
            <div className="flex flex-col items-center gap-2 text-white/80 mb-8">
              <Ban className="w-6 h-6" />
              <span className="text-xs">Mode Preview</span>
            </div>
          ) : (
            <>
              <Button
                size="sm"
                className="bg-mutagim-red hover:bg-red-700 text-white shadow-lg"
                onClick={handleAddToCart}
                data-testid="quick-add-cart"
              >
                <ShoppingCart className="h-4 w-4 mr-1" />
                {t('product.addToCart', 'Ajouter')}
              </Button>
              <Button
                size="icon"
                variant="secondary"
                className="bg-white/90 hover:bg-white text-mutagim-dark shadow-lg"
                data-testid="quick-view"
              >
                <Eye className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-col flex-1 p-3 space-y-1.5">
        {/* Product name */}
        <h3 className="text-white font-medium text-sm line-clamp-2 group-hover:text-mutagim-red transition-colors min-h-[2.5rem]">
          {name}
        </h3>
        
        {/* Rating + reviews */}
        {rating > 0 && (
          <div className="flex items-center gap-1">
            <div className="flex items-center gap-0.5">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  className={cn(
                    "h-3 w-3",
                    star <= Math.floor(rating) 
                      ? "fill-yellow-400 text-yellow-400" 
                      : star - 0.5 <= rating
                        ? "fill-yellow-400/50 text-yellow-400"
                        : "text-gray-600"
                  )}
                />
              ))}
            </div>
            <span className="text-xs text-gray-400">
              ({reviewCount})
            </span>
          </div>
        )}

        {/* Social proof - Vendus ce mois */}
        {soldCount > 0 && (
          <p className="text-xs text-gray-500 flex items-center gap-1" data-testid="sold-count">
            <Flame className="w-3 h-3 text-orange-400" />
            {t('product.soldThisMonth', '{{count}} vendus ce mois', { count: soldCount })}
          </p>
        )}

        {/* Price */}
        <div className="mt-auto pt-2 flex items-baseline gap-2">
          <span className="text-lg font-bold text-mutagim-red" data-testid="product-price">
            {formatPrice(product.pricing?.prices)}
          </span>
          {product.pricing?.compare_at_prices && (
            <span className="text-sm text-gray-500 line-through">
              {formatPrice(product.pricing.compare_at_prices)}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

export default ProductCardNetflix;
